<template>
  <b-modal
    id="new-admin-user-modal"
    ref="new-admin-user-modal"
    v-model="show"
    :title="!isEdit ? 'Add New User' : 'Edit User'"
    size="lg"
    hide-footer
    no-close-on-backdrop
    centered
    @hidden="handleHiddenModal"
  >
    <ValidationObserver
      ref="adminUserForm"
      v-slot="{ passes }"
    >
      <form @submit.prevent="passes(handleSubmit)">
        <b-row class="mt-2">
          <b-col md="12">
            <b-form-group
              label="Full Name"
            >
              <validation-provider
                v-slot="{ errors }"
                vid="name"
                name="Full Name"
              >
                <b-form-input
                  v-model="form.name"
                  placeholder="Enter Full Name (e.g. John Doe)"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Email Address"
            >
              <validation-provider
                v-slot="{ errors }"
                vid="email"
                name="Email Address"
              >
                <b-form-input
                  v-model="form.email"
                  placeholder="Enter Email Address"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Role"
            >
              <validation-provider
                v-slot="{ errors }"
                vid="ability_id"
                name="Role"
              >
                <v-select
                  v-model="form.ability_id"
                  :options="abilities"
                  :clearable="false"
                  label="title"
                  :reduce="ability => ability.id"
                  append-to-body
                  :calculate-position="withPopper"
                  placeholder="Select Role"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            v-if="isEdit"
            md="12"
          >
            <b-form-group
              label="Status"
            >
              <validation-provider
                v-slot="{ errors }"
                vid="status"
                name="Status"
              >
                <v-select
                  v-model="form.status"
                  :options="statuses"
                  :clearable="false"
                  label="text"
                  :reduce="status => status.value"
                  append-to-body
                  :calculate-position="withPopper"
                  placeholder="Select Status"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
            md="12"
          >
            <span class="text-info">Note: Random Password will be sent to the specified Email Address.</span>
          </b-col>
        </b-row>
        <b-button
          variant="primary"
          class="float-right my-1"
          type="submit"
          :disabled="isLoading"
        >
          <b-spinner
            v-if="isLoading"
            class="mr-50 p-0"
            small
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            class="mr-50"
          />
          {{ !isEdit ? 'Add New User' : 'Update User' }}
        </b-button>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { createPopper } from '@popperjs/core'
import vSelect from 'vue-select'
import axios from '@axios'

export default {
  name: 'AddEditAdminUserModal',
  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    isShow: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      default: () => {},
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    abilities: {
      type: Array,
      default: () => {},
    },
  },
  data() {
    return {
      show: false,
      isLoading: false,
      editSwalMessage: {
        icon: 'success',
        title: 'Updated',
        text: 'Successfully Updated Admin User',
        customClass: {
          confirmButton: 'btn btn-success',
        },
      },
      addSwalMessage: {
        icon: 'success',
        title: 'Created',
        text: 'Successfully Created Admin User',
        customClass: {
          confirmButton: 'btn btn-success',
        },
      },
      statuses: [
        {
          value: 0,
          text: 'In Active',
        },
        {
          value: 1,
          text: 'Active',
        },
      ],
    }
  },
  watch: {
    isShow(newValue) {
      this.show = newValue
    },
  },
  methods: {
    handleHiddenModal() {
      this.show = false
      this.$emit('reset-modal')
    },
    async handleSubmit() {
      try {
        this.isLoading = true
        const response = this.isEdit ? await axios.patch(`/admin-access/${this.form.id}`, this.form) : await axios.post('/admin-access', this.form)
        if (response.status === 422) {
          this.$refs.adminUserForm.setErrors(response.data.errors)
          this.isLoading = false
          return
        }
        if (response.status === 201 || response.status === 200) {
          this.isLoading = false
          this.$swal(this.isEdit ? this.editSwalMessage : this.addSwalMessage)
            .then(() => {
              this.handleHiddenModal()
              this.$emit('success')
            })
        }
      } catch (error) {
        throw new Error('Something went wrong.', error)
      }
    },
    withPopper(dropdownList, component, { width }) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      const tempDropDown = dropdownList
      tempDropDown.style.width = width
      tempDropDown.style.zIndex = '1300'

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, tempDropDown, {
        placement: 'top',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1],
            },
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top',
              )
            },
          },
        ],
      })

      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy()
    },
  },
}
</script>
